// IM-EXT-019 (IM-UI-084) — Portfólio e Ecossistema: lista das análises e geração. Abrir a área só lê o contexto
// (ofertas, ICPs, segmentação, concorrentes, preços, relações); nada é gerado, pesquisado ou enviado à IA até a pessoa
// clicar em "Gerar análise recomendada" (ou personalizar e gerar).
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { CreatePortfolioRequest, PortfolioListItemDto, PortfolioOptionsDto, PortfolioRecommendationDto } from '@intelimove/contracts';
import { Disclosure, ModuleIntro } from '../components/Guidance';
import { GenerationStatus, RecommendedContext, useSubmitOnce } from '../components/Recommendation';
import { AsyncView } from '../components/States';
import { useToast } from '../components/Toast';
import { PageHead, StatusPill } from '../components/Widgets';
import { PORTFOLIO_GUIDE } from '../data/guides';
import { fmtDate, portfolioApi } from '../data/portfolio';
import { useResource } from '../hooks/useResource';
import { useCanManage } from './Targets';

export function Portfolio() {
  const canManage = useCanManage();
  const [params, setParams] = useSearchParams();
  const creating = params.get('nova') === '1';
  const setCreating = (on: boolean) => { const p = new URLSearchParams(params); if (on) p.set('nova', '1'); else p.delete('nova'); setParams(p); };
  const { state, reload } = useResource((s) => portfolioApi.list(s), []);
  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Concorrentes e mercado" title="Portfólio e Ecossistema"
        subtitle="Quais ofertas priorizar, onde estão as lacunas, como seus preços se posicionam e quais parceiros e canais podem ampliar o alcance comercial — sempre com a evidência."
        actions={canManage && !creating ? <button className="cta" onClick={() => setCreating(true)}>Nova análise</button> : undefined} />
      <ModuleIntro guide={PORTFOLIO_GUIDE} />
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(d) => (
          <>
            {(creating || d.items.length === 0) && (canManage ? <CreateBox onCancel={d.items.length ? () => setCreating(false) : undefined} />
              : <div className="card notice" role="status">Nenhuma análise de portfólio ainda. Um administrador pode gerar a análise recomendada.</div>)}
            {d.items.length > 0 && <List items={d.items} />}
          </>
        )}
      </AsyncView>
    </section>
  );
}

function List({ items }: { items: PortfolioListItemDto[] }) {
  return (
    <div className="table-card">
      <div className="table-wrap">
        <table className="data-table stack">
          <caption className="sr-only">Análises de portfólio</caption>
          <thead><tr><th scope="col">Análise</th><th scope="col">Ofertas</th><th scope="col">Lacunas</th><th scope="col">Recomendações</th><th scope="col">Atualizada</th><th scope="col"><span className="sr-only">Ações</span></th></tr></thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id}>
                <td data-label="Análise"><b>{x.name}</b><span className="cell-sub">versão {x.versionNo} · {x.mode === 'RECOMENDADA' ? 'análise recomendada' : 'personalizada'}{x.status === 'ARCHIVED' ? ' · arquivada' : ''} · {x.regionLabel}</span></td>
                <td data-label="Ofertas">{x.summary?.offerings ?? '—'}</td>
                <td data-label="Lacunas">{x.summary ? <>{x.summary.gaps}{x.summary.confirmedGaps ? <span className="cell-sub">{x.summary.confirmedGaps} confirmada(s)</span> : null}</> : '—'}</td>
                <td data-label="Recomendações">{x.summary ? <>{x.summary.recommendations}{x.summary.highPriority ? <span className="cell-sub">{x.summary.highPriority} de prioridade alta</span> : null}</> : '—'}</td>
                <td data-label="Atualizada">{fmtDate(x.updatedAt, true)}</td>
                <td><Link className="small-action" to={`/portfolio/${x.id}`} aria-label={`Abrir ${x.name}`}>Abrir</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateBox({ onCancel }: { onCancel?: () => void }) {
  const { state, reload } = useResource(async (s) => { const [o, rec] = await Promise.all([portfolioApi.options(s), portfolioApi.recommendation(s)]); return { o, rec }; }, []);
  return (
    <div className="card seg-create">
      <h2 className="card-title" id="pf-new">Gerar a análise de portfólio</h2>
      {state.kind === 'loading' ? <p className="rc-status" role="status">Preparando o contexto da sua empresa…</p>
        : <AsyncView state={state} onRetry={reload} empty={null}>{({ o, rec }) => <CreateFields o={o} rec={rec} onCancel={onCancel} />}</AsyncView>}
    </div>
  );
}

function CreateFields({ o, rec, onCancel }: { o: PortfolioOptionsDto; rec: PortfolioRecommendationDto; onCancel?: () => void }) {
  const toast = useToast();
  const nav = useNavigate();
  const r0 = rec.request;
  const [name, setName] = useState(r0.name);
  const [offerings, setOfferings] = useState<string[]>(r0.scope.offeringIds);
  const [segmentationId, setSegmentationId] = useState(r0.scope.segmentationId ?? '');
  const [region, setRegion] = useState(r0.scope.region?.text ?? '');
  const [currency, setCurrency] = useState(r0.scope.currency);
  const [failure, setFailure] = useState<string | null>(null);
  const { busy, run } = useSubmitOnce();
  const request: CreatePortfolioRequest = { name: name.trim(), scope: { offeringIds: offerings, segmentationId: segmentationId || null, ...(region.trim() ? { region: { text: region.trim() } } : {}), currency } };
  const customized = JSON.stringify(request.scope) !== JSON.stringify({ offeringIds: r0.scope.offeringIds, segmentationId: r0.scope.segmentationId ?? null, ...(r0.scope.region?.text ? { region: { text: r0.scope.region.text } } : {}), currency: r0.scope.currency });
  const restore = () => { setOfferings(r0.scope.offeringIds); setSegmentationId(r0.scope.segmentationId ?? ''); setRegion(r0.scope.region?.text ?? ''); setCurrency(r0.scope.currency); };
  const noOfferings = o.offerings.length === 0;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void run(async () => {
      if (request.name.length < 3) { setFailure('Dê um nome com ao menos 3 caracteres.'); return; }
      setFailure(null);
      try {
        const d = await portfolioApi.create(request);
        toast(`Análise pronta: ${d.result.offerings.length} oferta(s), ${d.result.gaps.length} lacuna(s) e ${d.result.recommendations.length} recomendação(ões).`);
        nav(`/portfolio/${d.id}`);
      } catch (err) { setFailure((err as Error).message); }
    });
  };
  return (
    <form onSubmit={submit} aria-labelledby="pf-new" className="sz-form" aria-busy={busy}>
      <div className="gd-recommended" role="group" aria-labelledby="pf-rec-t">
        <p id="pf-rec-t"><b>{customized ? 'Análise personalizada' : 'Análise recomendada'}:</b> {name || '—'} {customized ? <StatusPill tone="amber">ajustada por você</StatusPill> : <StatusPill tone="mint">montada com o seu contexto</StatusPill>}</p>
        <p className="cell-sub wide">Usa o que o InteliMove já tem: ofertas, ICPs, segmentação, concorrentes, battlecards, análises de mercado, preços e relações registrados. Sem IA e sem pesquisa nova. Onde faltar dado, aparece como lacuna.</p>
      </div>
      <RecommendedContext values={rec.values} conflicts={[]} choices={{}} onChoose={() => undefined} requirements={rec.requirements}
        confidence={rec.expectedConfidence} confidenceReason={rec.confidenceReason} limitations={rec.limitations} />
      {noOfferings && <p className="notice-inline" role="alert">Nenhuma oferta confirmada no contexto. Cadastre as ofertas em <Link to="/configuracoes/contexto">Contexto da empresa</Link> antes de gerar.</p>}
      <Disclosure className="gd-advanced" title="Personalizar análise" hint="Configurações avançadas: nome, ofertas, segmentação, região e moeda, já preenchidos com a recomendação">
        <div className="seg-create-grid">
          <div className="field"><label htmlFor="pf-name">Nome</label><input id="pf-name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="field"><label htmlFor="pf-region">Região</label><input id="pf-region" maxLength={200} list="pf-regions" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Brasil, estado (PR) ou cidade (Curitiba/PR)" />
            <datalist id="pf-regions">{o.regionSuggestions.map((r) => <option key={r} value={r} />)}</datalist></div>
          <div className="field"><label htmlFor="pf-cur">Moeda de leitura</label><select id="pf-cur" value={currency} onChange={(e) => setCurrency(e.target.value as typeof currency)}><option value="BRL">BRL (real)</option><option value="USD">USD</option><option value="EUR">EUR</option></select>
            <span className="cell-sub wide">Nenhuma conversão cambial é feita: preços em outra moeda ficam “não comparáveis”.</span></div>
        </div>
        <div className="sz-scope">
          <fieldset className="sz-fieldset"><legend>Segmentação</legend>
            {o.segmentations.length ? <div className="field"><label htmlFor="pf-seg">Análise de segmentação</label>
              <select id="pf-seg" value={segmentationId} onChange={(e) => setSegmentationId(e.target.value)}><option value="">Nenhuma</option>{o.segmentations.map((s) => <option key={s.id} value={s.id}>{s.name} (v{s.versionNo})</option>)}</select></div>
              : <p className="cell-sub">Nenhuma segmentação ativa. A cobertura de segmentos fica de fora (menu Segmentação).</p>}
          </fieldset>
          <fieldset className="sz-fieldset"><legend>Ofertas</legend>
            <p className="cell-sub wide">Sem marcação, entram todas as ofertas ativas.</p>
            {o.offerings.map((x) => <label key={x.id} className="sz-check"><input type="checkbox" checked={offerings.includes(x.id)} onChange={() => setOfferings(offerings.includes(x.id) ? offerings.filter((y) => y !== x.id) : [...offerings, x.id])} /> {x.name}</label>)}
          </fieldset>
        </div>
        {customized && <button type="button" className="link-btn" onClick={restore}>Voltar à recomendação</button>}
      </Disclosure>
      <GenerationStatus busy={busy} error={failure} busyText="Gerando a análise com os dados registrados… leva alguns segundos." />
      <div className="form-actions">
        {onCancel && <button type="button" className="secondary" onClick={onCancel} disabled={busy}>Cancelar</button>}
        <button className="cta" disabled={busy || noOfferings}>{busy ? 'Gerando a análise…' : customized ? 'Gerar análise personalizada' : 'Gerar análise recomendada'}</button>
      </div>
    </form>
  );
}
